import { Link } from "react-router-dom";
import { ArrowRight, MapPin, ShieldCheck, BadgeCheck, Globe2 } from "lucide-react";
import { Reveal, MaskedReveal } from "../components/Reveal";
import { SITE, IMAGES } from "../lib/site";

const PageHeader = () => (
  <section className="relative bg-navy text-white overflow-hidden grain" data-testid="about-header">
    <div className="absolute inset-0 hero-grid" />
    <div className="absolute -top-32 right-0 w-96 h-96 rounded-full bg-royal/25 blur-[120px]" />
    <div className="relative max-w-7xl mx-auto px-5 sm:px-8 py-20 sm:py-28">
      <p className="text-xs uppercase tracking-[0.3em] font-bold text-gold">About Us</p>
      <h1 className="mt-4 font-display font-black tracking-tight leading-[1.02] text-4xl sm:text-5xl lg:text-6xl" data-testid="about-heading">
        <MaskedReveal delay={0.1}>The Company Behind</MaskedReveal>
        <MaskedReveal delay={0.22}>
          <span className="text-gold">RICHIE RICH</span>
        </MaskedReveal>
      </h1>
      <p className="mt-6 max-w-2xl text-slate-400 text-base sm:text-lg leading-relaxed">
        An IT company headquartered in Delhi, India — helping businesses go digital with enterprise-grade
        engineering, honest pricing and support that actually stays.
      </p>
    </div>
  </section>
);

const Story = () => (
  <section className="bg-ivory py-24" data-testid="about-story">
    <div className="max-w-7xl mx-auto px-5 sm:px-8 grid lg:grid-cols-2 gap-14 items-center">
      <div>
        <Reveal>
          <p className="text-xs uppercase tracking-[0.28em] font-bold text-royal">Our Story</p>
          <h2 className="mt-3 font-display text-3xl sm:text-4xl font-black text-ink tracking-tight">
            Built in Delhi. Trusted Everywhere.
          </h2>
        </Reveal>
        <Reveal delay={0.1}>
          <p className="mt-6 text-slate-600 leading-relaxed">
            RICHIE RICH started with a simple belief — Indian businesses deserve the same engineering quality that
            global enterprises get. Today, our team designs, builds and maintains digital products for startups,
            SMEs and enterprise clients across industries.
          </p>
          <p className="mt-4 text-slate-600 leading-relaxed">
            From corporate websites and mobile apps to cloud infrastructure, cybersecurity and the official
            WhatsApp Business API — we handle the full technology stack so our clients can focus on growth.
          </p>
        </Reveal>
        <div className="mt-10 grid sm:grid-cols-2 gap-5">
          {[
            { t: "Our Mission", d: "Make enterprise-grade technology accessible, affordable and reliable for every growing business." },
            { t: "Our Vision", d: "To be India's most trusted IT partner — measured by the success of the businesses we power." },
          ].map((v, i) => (
            <Reveal key={v.t} delay={0.15 + i * 0.08}>
              <div className="bg-white rounded-2xl border border-slate-200 p-6 h-full hover:border-gold/60 transition-colors" data-testid={`about-vision-card-${i}`}>
                <h3 className="font-display font-bold text-ink text-lg">{v.t}</h3>
                <p className="mt-2 text-sm text-slate-500 leading-relaxed">{v.d}</p>
              </div>
            </Reveal>
          ))}
        </div>
      </div>
      <Reveal delay={0.1}>
        <div className="relative">
          <div className="absolute -inset-3 rounded-[2rem] border-2 border-royal/25 rotate-1" />
          <img src={IMAGES.team} alt="RICHIE RICH office team" className="relative rounded-[1.75rem] w-full h-[30rem] object-cover shadow-2xl" data-testid="about-story-image" />
          <div className="absolute -bottom-6 -left-4 sm:-left-8 bg-navy text-white rounded-2xl px-6 py-5 shadow-2xl border border-white/10">
            <p className="font-display text-3xl font-black text-gold">500+</p>
            <p className="text-xs uppercase tracking-widest text-slate-300 mt-1">Projects Delivered</p>
          </div>
        </div>
      </Reveal>
    </div>
  </section>
);

const Values = () => (
  <section className="bg-white py-24 border-y border-slate-200" data-testid="about-values">
    <div className="max-w-7xl mx-auto px-5 sm:px-8">
      <Reveal>
        <p className="text-xs uppercase tracking-[0.28em] font-bold text-royal text-center">How We Work</p>
        <h2 className="mt-3 font-display text-3xl sm:text-4xl font-black text-ink tracking-tight text-center">
          Values We Engineer By
        </h2>
      </Reveal>
      <div className="mt-12 grid sm:grid-cols-2 lg:grid-cols-4 gap-5">
        {[
          { icon: ShieldCheck, t: "Security First", d: "Every build ships with hardened configs, encrypted data flows and ISO 27001-aligned practices." },
          { icon: BadgeCheck, t: "Radical Transparency", d: "Written scope, milestone billing and weekly progress demos. No surprises, ever." },
          { icon: Globe2, t: "Engineering Depth", d: "Modern stacks, code reviews and QA automation — the same rigour global product teams use." },
          { icon: MapPin, t: "Local Roots, Global Standards", d: "Delhi HQ with clients across India and overseas — available in your timezone, on your channel." },
        ].map((v, i) => (
          <Reveal key={v.t} delay={i * 0.08}>
            <div className="bg-ivory rounded-2xl border border-slate-200 p-7 h-full hover:-translate-y-1.5 hover:shadow-xl hover:shadow-royal/10 transition-all duration-300" data-testid={`about-value-card-${i}`}>
              <div className="w-12 h-12 rounded-xl bg-navy flex items-center justify-center">
                <v.icon className="text-gold" size={22} />
              </div>
              <h3 className="mt-5 font-display font-bold text-ink text-lg">{v.t}</h3>
              <p className="mt-2 text-sm text-slate-500 leading-relaxed">{v.d}</p>
            </div>
          </Reveal>
        ))}
      </div>
    </div>
  </section>
);

const Compliance = () => (
  <section className="bg-navy py-24 relative overflow-hidden grain" data-testid="about-compliance">
    <div className="absolute inset-0 hero-grid" />
    <div className="relative max-w-7xl mx-auto px-5 sm:px-8 grid lg:grid-cols-2 gap-14 items-center">
      <div>
        <Reveal>
          <p className="text-xs uppercase tracking-[0.28em] font-bold text-gold">Trust & Compliance</p>
          <h2 className="mt-3 font-display text-3xl sm:text-4xl font-black text-white tracking-tight">
            A Business You Can Trust
          </h2>
          <p className="mt-5 text-slate-400 leading-relaxed">
            We run RICHIE RICH the way we build software — documented, accountable and transparent. Our business
            presence, policies and communication channels are always open for clients and partners to review.
          </p>
        </Reveal>
        <div className="mt-8 space-y-4">
          {[
            "Official business email: " + SITE.email,
            "Dedicated phone line: " + SITE.phoneDisplay,
            "Registered office: " + SITE.address,
            "Privacy Policy & Terms published on this website",
          ].map((item, i) => (
            <Reveal key={item} delay={0.08 * i}>
              <div className="flex items-center gap-3 text-slate-300 text-sm" data-testid={`compliance-item-${i}`}>
                <BadgeCheck size={18} className="text-emerald-400 shrink-0" />
                {item}
              </div>
            </Reveal>
          ))}
        </div>
      </div>
      <Reveal delay={0.15}>
        <div className="relative rounded-[1.75rem] overflow-hidden border border-white/15 shadow-2xl">
          <img src={IMAGES.cloud} alt="RICHIE RICH cloud infrastructure" className="w-full h-[24rem] object-cover" data-testid="about-compliance-image" />
          <div className="absolute inset-0 bg-gradient-to-t from-navy/85 to-transparent" />
          <div className="absolute bottom-6 left-6 right-6">
            <p className="text-xs uppercase tracking-[0.22em] text-gold-light font-semibold">Headquarters</p>
            <p className="font-display text-2xl font-bold text-white mt-1">Delhi, India</p>
            <p className="text-slate-300 text-sm mt-1">Serving clients across India & overseas</p>
          </div>
        </div>
      </Reveal>
    </div>
  </section>
);

const About = () => (
  <>
    <PageHeader />
    <Story />
    <Values />
    <Compliance />
    <section className="bg-ivory py-20" data-testid="about-cta">
      <div className="max-w-7xl mx-auto px-5 sm:px-8 text-center">
        <Reveal>
          <h2 className="font-display text-3xl sm:text-4xl font-black text-ink tracking-tight">
            Want to Work With Us?
          </h2>
          <p className="mt-3 text-slate-500">One call or message — we will handle the rest.</p>
          <Link
            to="/contact"
            data-testid="about-cta-contact-button"
            className="mt-7 inline-flex items-center gap-2 bg-royal text-white font-bold px-8 py-3.5 rounded-full hover:bg-navy hover:-translate-y-1 transition-all duration-300 shadow-xl shadow-royal/25"
          >
            Get in Touch <ArrowRight size={17} />
          </Link>
        </Reveal>
      </div>
    </section>
  </>
);

export default About;
